/* eslint-disable */
import React, { useEffect } from "react";
import { FormattedHTMLMessage } from "react-intl";

import { shouldCollectPostalCode } from "../../lib/organization/organization";
import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import {
  CustomFormFieldConfig,
  Organization,
  TeacherPersonalInfoResponse,
  TeacherPersonalInfoViewModel,
  VerificationService,
} from "../../lib/types/types";
import {
  getFieldConfigByFieldId,
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";

import { getOptions } from "../../options/options";
import { ExtraMessage } from "../ExtraMessage";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { PostalCodeComponent } from "../FormFields/PostalCode/PostalCodeComponent";
import { TeacherSchoolComponent } from "../FormFields/TeacherSchool/TeacherSchoolComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { CaptchaComponent } from "../FormFields/Captcha/CaptchaComponent";

interface StepTeacherPersonalInfoComponentProps {
  verificationService: VerificationService<TeacherPersonalInfoViewModel>;
}

const StepTeacherPersonalInfo = ({
  verificationService,
}: StepTeacherPersonalInfoComponentProps) => {
  const { viewModel } = verificationService;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as TeacherPersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
  const options = getOptions();
  const locale = viewModel.localeChoice.value;
  const updateTeacherViewModel = (key: keyof TeacherPersonalInfoViewModel, value: any) => {
    const nextState = {
      [key]: value,
    };
    verificationService.updateViewModel(nextState, { partial: true });
  };

  // Don't validate DOB if ID Check is not enabled
  useEffect(() => {
    const existing = (verificationService.viewModel?.fieldsToSkipValidation || []).filter(
      (id) => id !== "birthDate",
    );
    if (!verificationService.programTheme.idCheckAddOnEnabled) {
      updateTeacherViewModel("birthDate", "");
      updateTeacherViewModel("fieldsToSkipValidation", [...existing, "birthDate"]);
    } else {
      updateTeacherViewModel("fieldsToSkipValidation", existing);
    }
  }, [verificationService.programTheme.idCheckAddOnEnabled]);

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="teacher-personal-info"
      />

      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />
      <TeacherSchoolComponent
        value={verificationService.viewModel.organization}
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.organization}
        disabled={getFieldConfigByFieldId("organization")?.disabled}
        onChange={(choice: Organization) => {
          updateViewModelOrganization(choice, verificationService);
          updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
        }}
      />
      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateTeacherViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateTeacherViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      {verificationService.programTheme.idCheckAddOnEnabled ? (
        <BirthDate
          errorId={fieldValidationErrors.birthDate}
          locale={locale}
          disabled={getFieldConfigByFieldId("birthDate")?.disabled}
          onChange={(newValue) => {
            updateTeacherViewModel("birthDate", newValue);
            updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
          }}
          value={viewModel.birthDate}
        />
      ) : null}

      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Must be your school-issued email address"
          />
        }
        onChange={(newValue) => {
          updateTeacherViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateTeacherViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
        />
      ) : null}
      {shouldCollectPostalCode(verificationService.programTheme, viewModel) ? (
        <PostalCodeComponent
          isErrored={!!fieldValidationErrors.postalCode}
          onChange={(newValue) => {
            updateTeacherViewModel("postalCode", newValue);
            updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
          }}
          value={viewModel.postalCode}
          disabled={getFieldConfigByFieldId("postalCode")?.disabled}
        />
      ) : null}
      {options.customFormFields.map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}
      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateTeacherViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
      />
      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

      <CaptchaComponent
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.captchaToken}
      />

      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectTeacherPersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify My Teacher Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepTeacherPersonalInfoComponent = StepTeacherPersonalInfo;
function userDidConvert(COLLECT_INFO_SUBMIT: any, COLLECT_INFO_SUBMISSION_TIME: any) {
  throw new Error("Function not implemented.");
}
